import React, { useState } from 'react';
import styled from 'styled-components';
import MachineriesAdd from './MachineriesAdd';
import MachineriesEdit from './MachineriesEdit';
import ExcelJS from 'exceljs';

/* global showSaveFilePicker */

const MACHINERIES_DATA = [
  {
    type: 'Tractor',
    acquisitionMode: 'Purchased',
    yearAcquired: '2019',
    acquisitionAmount: '₱1,200,000',
    status: 'Operational'
  },
  {
    type: 'Rice Thresher',
    acquisitionMode: 'Donated',
    yearAcquired: '2021',
    acquisitionAmount: '₱0',
    status: 'Under Maintenance'
  },
];

const Machineries = ({ onBack }) => {
  const [activeForm, setActiveForm] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const openAddForm = () => {
    setActiveForm('add');
    setSelectedRow(null);
  };

  const openEditForm = (item) => {
    setSelectedRow(item);
    setActiveForm('edit');
  };

  const closeForm = () => {
    setActiveForm(null);
    setSelectedRow(null);
  };

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Machineries');

    worksheet.mergeCells('A1:F1');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = 'UNITED LAT B3 MACHINERIES';
    titleCell.font = { name: 'Calibri', size: 20, bold: true };
    titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.addRow([]);

    const headerRow = ['No', 'Type', 'Acquisition Mode', 'Year Acquired', 'Acquisition Amount', 'Status'];
    worksheet.addRow(headerRow);

    const headerRowNumber = 3;
    worksheet.getRow(headerRowNumber).eachCell(cell => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2E7D32' } };
      cell.alignment = { horizontal: 'left', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: '000000' } },
        bottom: { style: 'thin', color: { argb: '000000' } },
        left: { style: 'thin', color: { argb: '000000' } },
        right: { style: 'thin', color: { argb: '000000' } },
      };
    });

    MACHINERIES_DATA.forEach((item, index) => {
      const row = worksheet.addRow([
        index + 1,
        item.type,
        item.acquisitionMode,
        item.yearAcquired,
        item.acquisitionAmount,
        item.status
      ]);

      row.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'thin', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
          right: { style: 'thin', color: { argb: '000000' } },
        };
        cell.alignment = {
          horizontal: 'left',
          vertical: 'top',
          wrapText: true,
        };
      });
    });

    worksheet.columns = [
      { key: 'no', width: 5 },
      { key: 'type', width: 18 },
      { key: 'acquisitionMode', width: 20 },
      { key: 'yearAcquired', width: 15 },
      { key: 'acquisitionAmount', width: 20 },
      { key: 'status', width: 18 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    if (window.isSecureContext && 'showSaveFilePicker' in window) {
      try {
        const options = {
          suggestedName: 'Machineries.xlsx',
          types: [{
            description: 'Excel File',
            accept: {
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
            }
          }]
        };

        const handle = await showSaveFilePicker(options);
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      } catch (err) {
        console.error('Error saving file:', err.name, err.message || err);
        if (err.name !== 'AbortError') {
          alert('Save failed due to an unexpected error. Check the console.');
        }
      }
    } else {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Machineries.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  };

  return (
    <Container>
      <BackButton onClick={onBack}>‹</BackButton>
      <Header>
        <Title>UNITED LAT B3 MACHINERIES</Title>
        <Actions>
          <SearchInput type="text" placeholder="Search..." />
          <ButtonGroup>
            <Button onClick={openAddForm}>IA Profiling Form</Button>
            <Button onClick={handleExport}>Export</Button>
          </ButtonGroup>
        </Actions>
      </Header>

      <Content>
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>No</th>
                <th>Type</th>
                <th>Acquisition Mode</th>
                <th>Year Acquired</th>
                <th>Acquisition Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {MACHINERIES_DATA.map((item, index) => (
                <tr key={index} onClick={() => openEditForm(item)}>
                  <td>{index + 1}</td>
                  <td>{item.type}</td>
                  <td>{item.acquisitionMode}</td>
                  <td>{item.yearAcquired}</td>
                  <td>{item.acquisitionAmount}</td>
                  <td>{item.status}</td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableWrapper>

        {activeForm === 'add' && <MachineriesAdd onClose={closeForm} />}
        {activeForm === 'edit' && selectedRow && (
          <MachineriesEdit data={selectedRow} onClose={closeForm} />
        )}
      </Content>
    </Container>
  );
};

export default Machineries;

// Styled Components
const Container = styled.div`
  padding: 80px 20px 20px;
  font-family: sans-serif;
  min-height: 100vh;
  box-sizing: border-box;
`;

const Content = styled.div`
  display: flex;
  gap: 20px;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const BackButton = styled.button`
  position: absolute;
  margin-bottom: 70px;
  top: 50px;
  left: 85px;
  background: transparent;
  border: none;
  font-size: 28px;
  font-weight: 900;
  cursor: pointer;
  color: #2e7d32;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px; 
  margin-bottom: 15px;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: bold;
  top: 50px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SearchInput = styled.input`
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const Button = styled.button`
  padding: 6px 10px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #388e3c;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    background-color: #2e7d32;
    color: white;
    padding: 10px;
    font-size: 14px;
  }

  td {
    padding: 10px;
    font-size: 14px;
    border: 1px solid #ddd;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  tbody tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  tbody tr:hover {
    background-color: #f1f1f1;
  }
`;
